import { useEffect } from 'react';
import { useAuth, SESSION_EXPIRED_KEY } from '../auth/AuthProvider';
import { Logo } from '../components/Logo';
import { readPendingClock, pendingClockNote } from '../timeTracking/pendingClock';

/**
 * On the login page, ACTIVATE any waiting service worker immediately (don't wait
 * for the next in-app navigation like <ApplyUpdateOnNavigate/>): a signed-out user has no unsaved state, and a
 * stale worker here can break the sign-in flow itself (it used to serve the SPA
 * shell for the /__/auth popup). Checks for a fresh deploy too, so a stuck
 * client self-heals on its next visit to this page.
 */
function useActivateWaitingSw() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let done = false;
    const activate = (reg: ServiceWorkerRegistration) => {
      const w = reg.waiting;
      if (!w || done) return;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!done) {
          done = true;
          window.location.reload();
        }
      }, { once: true });
      w.postMessage({ type: 'SKIP_WAITING' });
    };
    void navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      activate(reg);
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        nw?.addEventListener('statechange', () => {
          if (nw.state === 'installed') activate(reg);
        });
      });
      void reg.update();
    });
  }, []);
}

export function LoginPage() {
  const { state, signIn, signInError } = useAuth();
  useActivateWaitingSw();
  const isWrongDomain = state.status === 'wrong_domain';
  // One-shot "your session expired" note (set when max session age forced sign-out).
  let sessionExpired = false;
  try {
    sessionExpired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
    if (sessionExpired) sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  } catch {
    /* ignore */
  }
  const pendingClock = readPendingClock();

  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm space-y-12">
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <Logo themed className="h-16" />
          </div>
          <div className="space-y-2">
            <h1 className="font-display text-2xl tracking-tightest">
              DAAily Hub
            </h1>
            <p className="text-sm text-fg-muted">
              Sign in with your DAAily Workspace account
            </p>
          </div>
        </div>

        {sessionExpired && !isWrongDomain && (
          <div className="border-hairline border-line text-sm p-4 rounded-md text-fg-muted">
            For security, your session timed out. Please sign in again.
            {pendingClock && <span className="block mt-2 text-fg">{pendingClockNote(pendingClock)}</span>}
          </div>
        )}

        <div className="space-y-4">
          <button
            type="button"
            onClick={() => void signIn()}
            className="btn-primary w-full"
          >
            Sign in with Google
          </button>

          {signInError && !isWrongDomain && (
            <div className="border-hairline border-danger/30 text-sm p-4 rounded-md text-fg-muted">
              {signInError}
            </div>
          )}

          {isWrongDomain && (
            <div className="border-hairline border-danger/30 text-sm p-4 text-fg">
              <p className="font-medium text-danger mb-1">
                Sign-in restricted
              </p>
              <p className="text-fg-muted text-xs leading-relaxed">
                The account{' '}
                <span className="text-fg">{state.email}</span> is not part
                of the daaily.com workspace. Sign in again with your work
                Google account.
              </p>
            </div>
          )}
        </div>

        <p className="text-xs text-fg-faint text-center tracking-tighter">
          DAAily platforms AG — Falkenstrasse 11, 8008 Zürich
        </p>
      </div>
    </main>
  );
}
